import { ADD_EMPLOYEE_TO_GROUP } from '@/common/actions/create';
import {
  GET_ACCESS_GROUP_EMPLOYEES,
  GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES_NEW,
} from '@/common/actions/get';
import { SectionHead, TableFiltersBar } from '@/common/pages/accountEdit/usersAndGroups/components';
import { ModalAddItems } from '@/common/pages/accountEdit/usersAndGroups/modals/ModalAddItems';
import {
  formatUserFullName,
  getVariables,
  queryParamsInitial,
} from '@/common/pages/accountEdit/usersAndGroups/store';
import { useTableQueryParams } from '@/common/pages/accountEdit/usersAndGroups/store/useTableQueryParams';
import { accessGroupUsersFiltersConfig } from '@/common/pages/accountEdit/usersAndGroups/utils/tableFilters';
import { COLORS, OrderTypes } from '@/constants';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box, ButtonIcon, ICONS_NAME, Table } from '@servicepipe/ui-components';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const ALL_USERS_LIMIT = 3000;

export const EditAccessGroupUsers = ({ accessGroup, refetch: refetchAccessGroup }) => {
  const { id: accountId, groupId } = useParams();
  const { queryParams, onPageChange, onSortingChange, onFiltersChange } = useTableQueryParams({
    ...queryParamsInitial,
    sortKey: 'partnerClientAccountEmployeeId',
  });
  const [isAddUsersModalShow, setIsAddUsersModalShow] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);

  const [getAccessGroupEmployees, { data, loading }] = useLazyQuery(GET_ACCESS_GROUP_EMPLOYEES, {
    fetchPolicy: 'network-only',
  });

  const { data: accountEmployeesData, loading: accountEmployeesLoading } = useQuery(
    GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES_NEW,
    {
      variables: {
        pageSize: ALL_USERS_LIMIT,
        page: 1,
        partnerClientAccountId: [Number(accountId)],
        deletedAt: null,
      },
      skip: !isAddUsersModalShow || !accountId,
      fetchPolicy: 'network-only',
    }
  );

  const { data: groupEmployeesData } = useQuery(GET_ACCESS_GROUP_EMPLOYEES, {
    variables: {
      pageSize: ALL_USERS_LIMIT,
      page: 1,
      accessGroupId: Number(groupId),
      deletedAt: null,
    },
    skip: !isAddUsersModalShow || !groupId,
    fetchPolicy: 'network-only',
  });

  const [addEmployeeToGroup, { loading: adding }] = useMutation(ADD_EMPLOYEE_TO_GROUP, {
    onError: (error) => toast.error(error.message),
  });

  useEffect(() => {
    if (!groupId) {
      return;
    }

    const variables = getVariables(queryParams, { filterUserEmail: 'string' });
    getAccessGroupEmployees({
      variables: {
        ...variables,
        accessGroupId: Number(groupId),
        deletedAt: null,
      },
    });
  }, [queryParams, groupId]);

  const fetchEmployees = () => {
    const variables = getVariables(queryParams, { filterUserEmail: 'string' });
    return getAccessGroupEmployees({
      variables: {
        ...variables,
        accessGroupId: Number(groupId),
        deletedAt: null,
      },
    });
  };

  const handleRemoveUser = async (employeeId) => {
    setRemovingUserId(employeeId);

    try {
      await addEmployeeToGroup({
        variables: {
          accessGroupIds: [Number(groupId)],
          isAdd: false,
          userIds: [employeeId],
        },
      });
      toast.success('Success');
      await fetchEmployees();
      refetchAccessGroup?.();
    } finally {
      setRemovingUserId(null);
    }
  };

  const sortingInitial = {
    id: queryParams.sortKey || '',
    desc: queryParams.sortDirection === OrderTypes.Desc,
  };

  const columns = [
    {
      id: 'partnerClientAccountEmployeeId',
      width: 120,
      minWidth: 120,
      header: 'ID',
      body: 'partnerClientAccountEmployeeId',
      enableSorting: true,
    },
    {
      id: 'userEmail',
      header: 'Email',
      minWidth: 250,
      width: 250,
      body: ({ row }) => row.original.partnerClientAccountEmployee?.email,
    },
    {
      width: 140,
      minWidth: 140,
      id: 'firstName',
      header: 'First name',
      body: ({ row }) => row.original.partnerClientAccountEmployee?.name?.first ?? '-',
    },
    {
      width: 140,
      minWidth: 140,
      id: 'lastName',
      header: 'Last name',
      body: ({ row }) => row.original.partnerClientAccountEmployee?.name?.last ?? '-',
    },
    {
      id: 'actions',
      width: 50,
      minWidth: 50,
      meta: { justify: 'center', variant: 'action' },
      header: '',
      body: ({ row }) => {
        const employeeId = row.original.partnerClientAccountEmployeeId;
        return (
          <Box width="100%" justify="flex-end">
            <ButtonIcon
              size="small"
              variant="ghost"
              icon={ICONS_NAME.delete}
              isDisabled={removingUserId === employeeId}
              iconColor={COLORS.grayDark}
              onClick={() => handleRemoveUser(employeeId)}
            />
          </Box>
        );
      },
    },
  ];

  const alreadyAddedIds = (groupEmployeesData?.accessGroupEmployees?.items || []).map(
    (item) => item.partnerClientAccountEmployeeId
  );

  return (
    <Box gap="24px" direction="column">
      <SectionHead
        title="Users"
        buttonLabel="Add users"
        onButtonClick={() => setIsAddUsersModalShow(true)}
      />

      <Box direction="column" width="100%" minWidth="0">
        <TableFiltersBar
          filtersConfig={accessGroupUsersFiltersConfig}
          queryFilters={queryParams.filters ?? []}
          onFiltersChange={onFiltersChange}
        />

        <Table
          loading={loading}
          data={data?.accessGroupEmployees?.items || []}
          columns={columns}
          page={queryParams.page}
          size={queryParams.pageSize}
          totalPages={data?.accessGroupEmployees?.info?.totalPages || 1}
          totalCount={data?.accessGroupEmployees?.info?.totalCount || 0}
          onPageChange={onPageChange}
          onSortingChange={onSortingChange}
          initialStateSort={sortingInitial}
        />
      </Box>

      <ModalAddItems
        isShow={isAddUsersModalShow}
        title={`Add users to ${accessGroup?.name || 'group'}`}
        searchPlaceholder="Email"
        items={accountEmployeesData?.partnerClientAccountEmployees?.items || []}
        alreadyAddedIds={alreadyAddedIds}
        loading={accountEmployeesLoading}
        getId={(item) => item.id}
        getPrimary={(item) => item.email}
        getSecondary={(item) => formatUserFullName(item.name)}
        primaryHeader="Email"
        secondaryHeader="Name"
        onClose={() => setIsAddUsersModalShow(false)}
        submitting={adding}
        onAdd={async (userIds) => {
          await addEmployeeToGroup({
            variables: {
              accessGroupIds: [Number(groupId)],
              isAdd: true,
              userIds,
            },
          });
          toast.success('Success');
          setIsAddUsersModalShow(false);
          fetchEmployees();
          refetchAccessGroup?.();
        }}
      />
    </Box>
  );
};

export default EditAccessGroupUsers;
